import { useCallback, useEffect, useState } from "react";

import { Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Switch } from "../../components/ui/switch.jsx";
import { Label } from "../../components/ui/label.jsx";

import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { usePagination } from "../../hooks/usePagination";

import ActivityDialog from "./component/ActivityDialog";
import ActivityTable from "./component/ActivityTable";

const Activities = () => {
  const { user } = useAuth();
  const { projects, users, globalProjectId } = useData();
  const api = useApi();
  const navigate = useNavigate();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const [editingActivityId, setEditingActivityId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [direction, setDirection] = useState("desc");

  const pagination = usePagination(10);
  const { items: activities, currentPage: page, pageSize, setPaginationData, setCurrentPage: setPage } = pagination;

  const [newActivity, setNewActivity] = useState({
    projectId: "",
    date: "",
    title: "",
    description: "",
    assignedTo: ""
  });

  const loadActivities = useCallback(async () => {
    try {
      const response = await api.get(`/activities`, {
        params: {
          projectId: globalProjectId === "ALL" ? "" : globalProjectId,
          page,
          size: pageSize,
          search: searchTerm.trim() || undefined,
          sortBy: sortBy || undefined,
          direction: direction || undefined,
          showDeleted: showDeleted
        }
      });
      setPaginationData(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to load activities");
    }
  }, [api, globalProjectId, page, pageSize, searchTerm, sortBy, direction, showDeleted, setPaginationData]);

  const createActivity = async (e) => {
    e.preventDefault();
    try {
      if (editingActivityId) {
        // Update existing activity
        await api.put(`/activities/${editingActivityId}`, {
          ...newActivity,
          date: newActivity.date ? new Date(newActivity.date).toISOString() : null
        });
        toast.success("Activity updated successfully!");
      } else {
        // Create new activity
        await api.post(`/activities`, {
          ...newActivity,
          date: new Date(newActivity.date).toISOString()
        });
        toast.success("Activity created successfully!");
      }
      loadActivities();

      setNewActivity({
        projectId: "",
        date: "",
        title: "",
        description: "",
        assignedTo: ""
      });
      setEditingActivityId(null);
      setIsCreateOpen(false);
    } catch (error) {
      toast.error(error.message || (editingActivityId ? "Failed to update activity" : "Failed to create activity"));
    }
  };

  const editActivity = async (activityId) => {
    try {
      const response = await api.get(`/activities/${activityId}`);
      const activityData = response.data;

      // Format date for date input (YYYY-MM-DD)
      const formattedDate = activityData.date ? new Date(activityData.date).toISOString().split("T")[0] : "";

      // Set field values from response
      setNewActivity({
        projectId: activityData.project?.id || "",
        date: formattedDate,
        title: activityData.title || "",
        description: activityData.description || "",
        assignedTo: activityData.assignedTo?.id || ""
      });
      setEditingActivityId(activityId);
      setIsCreateOpen(true);
    } catch (error) {
      setIsCreateOpen(false);
      toast.error(error.message || "Failed to fetch activity details");
    }
  };

  const updateActivityStatus = async (activityId, status) => {
    try {
      const updatedActivity = {
        status: status
      };

      await api.put(`/activities/${activityId}/status`, updatedActivity, {
        headers: { "Content-Type": "application/json" }
      });
      loadActivities();

      toast.success("Activity status updated!");
    } catch (error) {
      toast.error(error.message || "Failed to update activity status");
    }
  };

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-500">
      <div className="flex-1 space-y-6">
        <PageHeader
          title="Activity Log"
          subtitle="Track construction progress and daily logs"
          actionLabel={user?.permissions?.activity?.create ? "Log Activity" : null}
          onActionClick={() => setIsCreateOpen(true)}
          pageIcon={Activity}
          extraActions={
            <div className="flex items-center gap-4">
              <SearchInput
                placeholder="Search activities..."
                className="md:w-72"
                onSearch={(val) => {
                  setSearchTerm(val);
                  setPage(0);
                }}
              />
            </div>
          }
        />

        <ActivityDialog
          open={isCreateOpen}
          onOpenChange={(open) => {
            setIsCreateOpen(open);
            if (!open) {
              setEditingActivityId(null);
              setNewActivity({
                projectId: "",
                date: "",
                title: "",
                description: "",
                assignedTo: ""
              });
            }
          }}
          editingActivityId={editingActivityId}
          newActivity={newActivity}
          setNewActivity={setNewActivity}
          onSubmit={createActivity}
        />

        <ActivityTable
          activities={activities}
          onEdit={editActivity}
          onUpdateStatus={updateActivityStatus}
          sortBy={sortBy}
          direction={direction}
          onSort={(key, dir) => {
            setSortBy(key);
            setDirection(dir);
            setPage(0);
          }}
          showDeleted={showDeleted}
          setShowDeleted={setShowDeleted}
          setPage={setPage}
        />
      </div>

      <div className="mt-8">
        <Pagination pagination={pagination} />
      </div>
    </div>
  );
};

export default Activities;
